// 'did you know?' — a rotating card of tetris history + trivia that
// fills the dead space under the next queue during play. Facts
// crossfade on a slow timer; clicking the card advances immediately.
// Long facts that overflow the visible box slowly scroll up so the
// whole entry can be read without growing the card.
//
// Curated for accuracy — every entry is real tetris history or
// guideline-era trivia. No gameplay tips for unimplemented modes;
// no references to features specific to this build.

export const TETRIS_FACTS: string[] = [
  // ── Origins ────────────────────────────────────────────────────────
  'tetris was created in 1984 by alexey pajitnov at the soviet academy of sciences in moscow, on an elektronika 60 with no graphics — the first pieces were drawn from bracket characters.',
  "the name fuses 'tetra' (greek for four) with 'tennis' — pajitnov's favorite sport.",
  'the first color pc version was ported in 1984 by vadim gerasimov, a sixteen-year-old student who shared a workroom with pajitnov.',
  'tetris was the first video game exported from the soviet union to the west.',
  "the soviet state owned the rights to tetris until 1996. pajitnov saw almost nothing for the first decade — until the rights reverted and he co-founded the tetris company with henk rogers to finally own his own creation.",

  // ── The rights saga ────────────────────────────────────────────────
  'henk rogers convinced nintendo to bundle tetris with the 1989 game boy, arguing mario would sell the console to boys — but tetris would sell it to everyone.',
  "in 1989 henk rogers flew to moscow on a tourist visa and walked into elorg, the soviet software ministry, completely unannounced — to bid for the handheld rights to tetris. he was competing against atari's robert stein and mirrorsoft's kevin maxwell, and won the deal that put the game on the game boy.",
  "tengen released an unlicensed nes tetris in 1989 against nintendo's wishes; it was recalled within weeks after the rights battle. sealed copies are collector grails today, sometimes selling for thousands.",
  "the 2023 film 'tetris' dramatizes the cold-war scramble for the game's rights — rogers, pajitnov, the maxwells, atari, and the soviet bureaucracy all colliding inside a single moscow ministry building.",

  // ── Numbers, sales, ubiquity ───────────────────────────────────────
  'game boy tetris went on to sell roughly 35 million copies — one of the best-selling video games of all time.',
  "guinness has listed tetris among the most-ported video games in history — it has appeared on well over 200 platforms, from graphing calculators to oscilloscopes.",
  "people have played tetris on oscilloscopes, business cards, pumpkins, and the sides of skyscrapers. in 2014 a 29-story philadelphia tower hosted the world's largest playable game of tetris — visible for miles.",
  "the iconic 'type a' theme is korobeiniki, a 19th-century russian folk song about a young peddler bartering with a girl in the countryside.",

  // ── The tetris effect (the phenomenon) ─────────────────────────────
  "the 'tetris effect' was first described by players who, after long sessions, started seeing falling pieces in their daydreams and dreams. psychologists now use the term for any activity that colonizes idle thought.",
  'a 2017 oxford university study found that playing tetris within hours of a traumatic event measurably reduced the number of intrusive flashback memories that followed — the visual-spatial focus appears to interfere with how the memory consolidates.',
  'brain-imaging research found that practiced tetris players burn LESS glucose while playing than novices do — the brain literally rewires to handle the game more efficiently over time.',
  "a plymouth university study found that just a few minutes of tetris measurably dulls cravings — researchers think the visual workload crowds out the imagery the craving relies on.",

  // ── Classic / nes culture ──────────────────────────────────────────
  "nes tetris was long thought to end at level 29 — the speed at which the pieces outrun a normal controller. then players invented hypertapping (vibrating the d-pad finger like a hummingbird), and later rolling, and pushed the game decades past where its designers thought it ended.",
  "'rolling' — drumming the fingers across the back of the controller to bounce the d-pad — pushed classic players past twenty taps per second and shattered every nes tetris record set with hypertapping.",
  "on december 21, 2023, thirteen-year-old willis 'blue scuti' gibson became the first human in history to crash nes tetris — pushing the game past level 157 until the code itself ran out of memory and froze. nintendo confirmed the achievement; jonas neubauer's tribute video for him is one of the most-watched moments in classic tetris history.",
  'the classic tetris world championship began in 2010 at the portland retro gaming expo. the late jonas neubauer won seven of its first eight titles and is widely considered the player who built the modern classic scene.',
  "past level 138, nes tetris palettes glitch into murky near-invisible color sets — players call the worst of these stretches 'charcoal'. clearing lines without being able to see the stack apart from the playfield is a baseline skill at the top of the classic scene.",
  "the 2011 documentary 'ecstasy of order: the tetris masters' follows the original hunt to crown the world's best classic tetris player, before the ctwc became a yearly fixture.",

  // ── Guideline-era trivia ───────────────────────────────────────────
  'guideline colors are fixed worldwide: i is cyan, o yellow, t purple, s green, z red, j blue, l orange. that color contract is older than any current tetris client.',
  'modern versions deal pieces with a 7-bag randomizer — every seven pieces is a shuffled set containing each tetrimino exactly once. you can never be starved of the longbar for more than twelve pieces in a row.',
  'the super rotation system (srs) defines five kick tests per rotation, the spec that makes t-spin setups possible at all. earlier rotation systems (nintendo, sega, arika) all kick differently — each system has its own competitive scene.',
  'a t-spin triple scores more than a tetris under guideline rules — it requires sliding the t-piece into a notch only kick tests can reach, and is the highest-value standard clear in the modern game.',
  "clearing every cell on the board is a 'perfect clear' (also called 'all clear'). under guideline scoring, a back-to-back tetris perfect clear is the single most valuable scoring move in the game.",
  "'b2b' (back-to-back) chains link consecutive tetrises or t-spins with no lesser clear between them, boosting every clear in the chain — the longer the chain, the bigger the multiplier.",
  "the playfield is officially 10 wide and 40 tall, but only 20 rows are visible. the hidden buffer above is where pieces spawn — and where engines like this one track them too.",
  'hold queues, ghost pieces, and multi-piece previews are all guideline-era standards from the early 2000s. classic versions had none of them — you played with whatever next piece the rng handed you.',
  "the official spelling of the seven pieces is 'tetrimino'; mathematicians spell the general shape family 'tetromino'. both refer to a polyomino made of exactly four cells.",
  'there are exactly seven one-sided tetrominoes (the standard piece set). allow mirror flips and the s/z and j/l pairs collapse into each other, leaving only five.',
  "players call the i-piece 'the longbar'; building flat with a single-column well reserved for it is the oldest tetris strategy in the book, dating to pre-guideline play.",

  // ── Arcade lineage, tgm, deep cuts ─────────────────────────────────
  "sega's 1988 arcade tetris defined competitive play in japan, and its physics and feel live on in the tetris: the grand master series.",
  "tetris: the grand master (1998) ends with an invisible credits roll — pieces vanish the moment they lock, leaving the player to clear a screen they can't see. the players who clear it are the namesake grand masters.",
  "at '20g' gravity, pieces spawn already touching the top of the stack — there is no falling. tgm players navigate the entire board with kicks and muscle memory, never relying on falling speed.",

  // ── Pajitnov's other games, parodies ───────────────────────────────
  "welltris, pajitnov's 1989 sequel, drops pieces down the inside walls of a three-dimensional well — four playfields sharing a single bottom.",
  'pajitnov followed tetris with hatris in 1990, where the pieces are stacked hats instead of blocks. it sold poorly and is mostly remembered as a curio today.',
  "'not tetris' is a beloved physics parody where pieces tumble, tilt, and never quite line up — gravity is real and the stack drifts as you play.",

  // ── Modern releases ────────────────────────────────────────────────
  'tetris 99 (2019, nintendo switch) reimagined the game as a battle royale: 98 opponents on one screen, four targeting strategies, one winner — proof that the half-century-old core was still inventing new modes.',
  "tetris effect (2018, enhance) wraps the game in synesthetic light, color, and music — every clear plays a different note in the soundtrack. its title is a nod to the daydream phenomenon, and a love letter to the original.",
  'community-built clients and fan tournaments drive much of the modern competitive scene, running alongside the official releases — much of the rolling, kicking, and stacking technique vocabulary was invented in those communities.',
];

// Base rotation for facts that fit on screen — facts stay up long
// enough to be read at a glance several times over before swapping
// in the next entry. Scrolling facts compute their own total duration
// from the overflow distance plus generous top/bottom holds.
const ROTATE_MS = 42000;

export class FactsCard {
  readonly root: HTMLElement;
  /** Outer viewport — fixed-size, clips overflow. */
  private body: HTMLElement;
  /** Inner text element — what actually translates upward during scroll.
   *  Transforming the outer box would just move the whole card; the
   *  scroll has to happen on a child WITHIN the clipping viewport. */
  private inner: HTMLElement;
  private order: number[] = [];
  private cursor = 0;
  private timer: number | null = null;
  private scrollRaf: number | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'fact-box';
    this.root.style.display = 'none';
    const head = document.createElement('div');
    head.className = 'fact-head';
    head.textContent = 'did you know?';
    this.body = document.createElement('div');
    this.body.className = 'fact-body';
    this.inner = document.createElement('div');
    this.inner.className = 'fact-text';
    this.body.appendChild(this.inner);
    this.root.appendChild(head);
    this.root.appendChild(this.body);
    // Click anywhere on the card for the next fact.
    this.root.addEventListener('click', () => this.advance());
    this.shuffle();
  }

  private shuffle(): void {
    this.order = TETRIS_FACTS.map((_, i) => i);
    for (let i = this.order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const t = this.order[i]!; this.order[i] = this.order[j]!; this.order[j] = t;
    }
    this.cursor = 0;
  }

  private advance(): void {
    this.cursor++;
    if (this.cursor >= this.order.length) this.shuffle();
    this.swapTo(TETRIS_FACTS[this.order[this.cursor]!]!);
  }

  private clearTimer(): void {
    if (this.timer != null) { clearTimeout(this.timer); this.timer = null; }
  }

  private cancelScroll(): void {
    if (this.scrollRaf != null) { cancelAnimationFrame(this.scrollRaf); this.scrollRaf = null; }
    this.inner.style.transform = '';
  }

  /** Replace the current fact with a fade-out → fade-in, then measure
   *  whether the new text overflows the box. If it does, JS drives a
   *  slow scroll (top hold → ease-in-out scroll → bottom hold) and the
   *  next rotation is delayed until the whole entry has been read.
   *  Short facts use the base ROTATE_MS pause. */
  private swapTo(text: string): void {
    this.cancelScroll();
    this.clearTimer();
    this.body.classList.remove('in');
    window.setTimeout(() => {
      this.inner.textContent = text;
      this.body.classList.add('in');
      // Wait for the layout to settle before measuring overflow. The
      // INNER element's height is the true content height; the outer
      // .fact-body box clips it.
      requestAnimationFrame(() => {
        const overflow = this.inner.scrollHeight - this.body.clientHeight;
        const nextMs = overflow > 6 ? this.startScroll(overflow) : ROTATE_MS;
        this.timer = window.setTimeout(() => this.advance(), nextMs);
      });
    }, 320);
  }

  /** Drive a slow scroll over the overflowing text. Top hold lets the
   *  reader catch the first line; bottom hold leaves the last line
   *  parked before we move on. Returns the total duration (including
   *  a small post-scroll pause) so the rotation timer can match. */
  private startScroll(distancePx: number): number {
    // Long, leisurely paces — the trivia should feel calm-companion-y,
    // not a stock ticker. Top/bottom holds give well over a heartbeat
    // to read the first and last line; the scroll itself moves at
    // ~9px per second so a line takes ~2s to climb out.
    const TOP_HOLD = 4500;
    const BOT_HOLD = 5500;
    const SCROLL_MS = Math.max(5500, Math.round(distancePx * 110));
    const TOTAL = TOP_HOLD + SCROLL_MS + BOT_HOLD;
    const startTime = performance.now();
    const tick = (now: number) => {
      const t = now - startTime;
      if (t < TOP_HOLD) {
        this.inner.style.transform = 'translateY(0)';
      } else if (t < TOP_HOLD + SCROLL_MS) {
        const p = (t - TOP_HOLD) / SCROLL_MS;
        // Smooth ease-in-out so the scroll starts and stops gently.
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        this.inner.style.transform = `translateY(${-distancePx * eased}px)`;
      } else {
        this.inner.style.transform = `translateY(${-distancePx}px)`;
      }
      if (t < TOTAL) this.scrollRaf = requestAnimationFrame(tick);
      else this.scrollRaf = null;
    };
    this.scrollRaf = requestAnimationFrame(tick);
    // Generous read pause at the bottom before swapping in the next.
    return TOTAL + 2200;
  }

  show(): void {
    if (this.root.style.display !== 'none') return;
    this.root.style.display = '';
    this.swapTo(TETRIS_FACTS[this.order[this.cursor]!]!);
  }

  hide(): void {
    if (this.root.style.display === 'none') return;
    this.root.style.display = 'none';
    this.clearTimer();
    this.cancelScroll();
  }

  /** Fills the dead zone under the queue; hides itself when the layout
   *  leaves too little room to read a sentence. */
  position(left: number, top: number, width: number, height: number): void {
    if (height < 96 || width < 110) {
      this.root.style.visibility = 'hidden';
      return;
    }
    this.root.style.visibility = '';
    this.root.style.left = `${Math.round(left)}px`;
    this.root.style.top = `${Math.round(top)}px`;
    this.root.style.width = `${Math.round(width)}px`;
    this.root.style.height = `${Math.round(height)}px`;
  }
}
